import React, { useState } from "react";
import { AddRounded, DeleteOutlineRounded, EditRounded, RuleOutlined, VisibilityRounded } from "@mui/icons-material";
import { Box, Button, CardActions, CardContent, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { deleteRule, getRules } from "../../../services/landing_page/landing_page";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { DeleteRuleDialog } from "./delete-rule-dialog";
import LandingPageLayout from "../../../layouts/landing_page/landing_page_layout";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../users/components/table_toolkit/table_toolkit";
import { black, greyEnabled, purple, purpleLight } from "../../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const normalizeRulesResponse = (raw: unknown): { list: any[]; total: number } => {
  if (!raw) return { list: [], total: 0 };
  if (Array.isArray(raw)) return { list: raw, total: raw.length };
  const obj = raw as Record<string, unknown>;
  const list = obj.list ?? obj.data ?? [];
  const total = typeof obj.total === "number" ? obj.total : (Array.isArray(list) ? list.length : 0);
  return { list: Array.isArray(list) ? list : [], total };
};

export const RulesList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useFeedback();
  const queryClient = useQueryClient();
  const [searchParams, setUrlParam] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const currentTab = location.pathname.includes("/rules") ? 1 : 0;

  const [paginationModel, setPaginationModel] = useState({
    pageSize: ROWS_PER_PAGE_OPTIONS[0],
    page: searchParams.has("page") ? parseInt(searchParams.get("page")!, 10) : 1,
  });

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPaginationModel({ ...paginationModel, page: newPage + 1 });
    setUrlParam({
      page: (newPage + 1).toString()
    }, { replace: true })
  };

  const handleEditRule = (rule: any) => {
    navigate(`/rules/edit-rule/${rule.id || rule.content_id}`, {
      state: {
        ruleData: rule,
      },
    });
  };

  const handleViewRule = (rule: any) => {
    navigate(`/rules/view-rule/${rule.id || rule.content_id}`, {
      state: {
        ruleData: rule,
      },
    });
  };

  const handleDeleteClick = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    setIsDeleting(true);
    try {
      await deleteRule(selectedItem.id || selectedItem.content_id);
      showSnackbar("Rule deleted successfully!");
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      await queryClient.invalidateQueries({ queryKey: ["rules"] });
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error?.message || "Failed to delete rule. Please try again.";
      showSnackbar(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      navigate('/landing-page');
    } else if (newValue === 1) {
      navigate('/rules');
    }
  };

  const { data: rulesData, isFetching: isLoading, isError } = useQuery({
    queryKey: ["rules", paginationModel.page, paginationModel.pageSize],
    queryFn: async () => {
      const response = await getRules({
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
      const raw = response?.data?.data;
      return normalizeRulesResponse(raw);
    },
    onError: () => {
      showSnackbar("Failed to fetch rules");
    },
    keepPreviousData: true,
  });

  const data = rulesData ?? { list: [], total: 0 };
  const list = data?.list || [];
  const count = data?.total || 0;

  const startRange = (paginationModel.page - 1) * paginationModel.pageSize + 1;
  const endRange = Math.min(paginationModel.page * paginationModel.pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;

  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <LandingPageLayout
      page={paginationModel.page}
      setPage={(page) => setPaginationModel((prev) => ({ ...prev, page }))}
      title="Landing Page Rules"
      subtitle="Define audience targeting rules for content pages."
      icon={<RuleOutlined sx={{ color: purple, fontSize: 24 }} />}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 2.5, sm: 3 } }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ "& .MuiTab-root": { textTransform: "none", fontSize: "1rem", fontWeight: 500 } }}
        >
          <Tab label="Contents" />
          <Tab label="Rules" />
        </Tabs>
      </Box>

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box display="flex" alignItems="center" justifyContent="flex-end" mb={2}>
          <Button
            onClick={() => navigate("/rules/add-rule")}
            variant="contained"
            disableElevation
            startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
            sx={{
              bgcolor: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            Add Rule
          </Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Typography color={greyEnabled}>Loading...</Typography>
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the rules. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No rules found"
            hint="Add a rule to target content pages at a specific audience."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.3 },
                { label: "Rule Name", flex: 1.5 },
                { label: "Content Name", flex: 1.5 },
                { label: "Created On", flex: 0.7 },
                { label: "Actions" },
              ]}
              lastColumnWidth={150}
              align="left"
              bordered
            />
            {list.map((item: any, index: number) => (
              <TableRow key={item.id || item.content_id || index} bordered>
                <TableCell label="Sl. No." flex={0.3} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {(paginationModel.page - 1) * paginationModel.pageSize + index + 1}
                  </Typography>
                </TableCell>
                <TableCell label="Rule Name" flex={1.5} bordered>
                  <Tooltip title={item?.name || "-"} placement="top" arrow>
                    <Typography fontWeight={600} color={black} noWrap textOverflow="ellipsis" overflow="hidden">
                      {item?.name || "-"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell label="Content Name" flex={1.5} bordered>
                  <Tooltip title={item?.content_name || "-"} placement="top" arrow>
                    <Typography fontSize={14} color={black} noWrap textOverflow="ellipsis" overflow="hidden">
                      {item?.content_name || "-"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell label="Created On" flex={0.7} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {item?.createdAt?.split("T")[0] || "-"}
                  </Typography>
                </TableCell>
                <TableCell isLast lastColumnWidth={150} bordered>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    <Tooltip title="View rule">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewRule(item);
                        }}
                        sx={{ bgcolor: "#E7F5EC", "&:hover": { bgcolor: "#D6EEE0" } }}
                      >
                        <VisibilityRounded sx={{ color: "#1E6446", fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit rule">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRule(item);
                        }}
                        sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                      >
                        <EditRounded sx={{ color: purple, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete rule">
                      <IconButton
                        onClick={(e) => handleDeleteClick(item, e)}
                        sx={{ bgcolor: "#FBE7E4", "&:hover": { bgcolor: "#FBD9D6" } }}
                      >
                        <DeleteOutlineRounded sx={{ color: "#C0392B", fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={paginationModel.page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={paginationModel.pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(newSize) => {
                setPaginationModel({ page: 1, pageSize: newSize });
              }}
            />
          </Box>
        </CardActions>
      )}

      <DeleteRuleDialog
        open={deleteDialogOpen}
        ruleName={selectedItem?.name || selectedItem?.content_name}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </LandingPageLayout>
  );
};
